import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import {
  IDedDetailSelectItem,
  useGetDedDetailsSelectApi,
  useUpdateMappingLicenseApi,
} from 'apis/crm-brand-trade-license-mappings';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiCheck, FiSearch } from 'react-icons/fi';

interface LicenseMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  mappingId: number;
  currentLicenseNumber?: string;
  entityName: string;
  tradeLicenseName: string;
}

interface FormData {
  selectedDedDetail: IDedDetailSelectItem | null;
  licenseConfidence: number;
}

const defaultValues: FormData = {
  selectedDedDetail: null,
  licenseConfidence: 95,
};

export const LicenseMappingModal: React.FC<LicenseMappingModalProps> = ({
  isOpen,
  onClose,
  mappingId,
  currentLicenseNumber,
  entityName,
  tradeLicenseName,
}) => {
  const toast = useCustomToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDetail, setSelectedDetail] = useState<IDedDetailSelectItem | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
  });

  // Fetch DED details for selection
  const { data: dedDetails = [], isLoading: isLoadingDedDetails } = useGetDedDetailsSelectApi({
    search: searchTerm,
    limit: 50,
  });

  // Update license mapping mutation
  const { mutate: updateLicenseMapping, isLoading: isUpdating } = useUpdateMappingLicenseApi();

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    setSearchTerm('');
    setSelectedDetail(null);
    reset(defaultValues);
    onClose();
  }, [reset, onClose]);

  // Filter DED details based on search
  const filteredDedDetails = useMemo(() => {
    if (!searchTerm.trim()) return dedDetails;

    const searchLower = searchTerm.toLowerCase();
    return dedDetails.filter(
      (detail) =>
        detail.businessName?.toLowerCase().includes(searchLower) ||
        detail.licenseNumber?.toLowerCase().includes(searchLower) ||
        detail.label?.toLowerCase().includes(searchLower)
    );
  }, [dedDetails, searchTerm]);

  // Handle DED detail selection
  const handleSelectDetail = (detail: IDedDetailSelectItem) => {
    setSelectedDetail(detail);
  };

  // Handle form submission
  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!selectedDetail) {
        toast({
          title: 'No License Selected',
          description: 'Please select a trade license before proceeding.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const payload = {
        licenseNumber: selectedDetail.licenseNumber,
        licenseConfidence: data.licenseConfidence / 100, // Convert percentage to decimal
      };

      try {
        await new Promise<void>((resolve, reject) => {
          updateLicenseMapping(
            { id: mappingId, data: payload },
            {
              onSuccess: () => {
                // Close modal after successful update
                setTimeout(() => onCloseModal(), 100);
                resolve();
              },
              onError: (error) => reject(error),
            }
          );
        });
      } catch (error) {
        // Error handling is already done in the API
        console.error('Failed to update license mapping:', error);
      }
    },
    [selectedDetail, mappingId, updateLicenseMapping, toast, onCloseModal]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='4xl' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Map Trade License - {entityName}</ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <VStack spacing={6} align='stretch'>
            {/* Restaurant Reference Information */}
            <Box p={4} bg='gray.50' borderRadius='lg' border='1px solid' borderColor='gray.200'>
              <VStack spacing={3} align='stretch'>
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color='gray.600'
                  textTransform='uppercase'
                  letterSpacing='wide'>
                  Restaurant Reference
                </Text>
                <HStack spacing={4} justify='space-between' flexWrap='wrap'>
                  <VStack align='start' spacing={1} flex='1' minW='200px'>
                    <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                      Restaurant Name
                    </Text>
                    <Text fontSize='md' fontWeight='semibold' color='blue.700' lineHeight='1.2'>
                      {entityName}
                    </Text>
                  </VStack>
                  <VStack align='start' spacing={1} flex='1' minW='200px'>
                    <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                      Legal Name
                    </Text>
                    <Text fontSize='md' fontWeight='semibold' color='purple.600' lineHeight='1.2'>
                      {tradeLicenseName}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Current License Info */}
            {currentLicenseNumber && (
              <Box p={4} bg='blue.50' borderRadius='md' border='1px solid' borderColor='blue.200'>
                <Text fontSize='sm' fontWeight='medium' color='blue.800'>
                  Current License: {currentLicenseNumber}
                </Text>
              </Box>
            )}

            {/* Search Section */}
            <Box>
              <FormLabel>Search Trade Licenses</FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <FiSearch color='gray.400' />
                </InputLeftElement>
                <Input
                  placeholder='Search by business name or license number...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Box>

            {/* Search Results */}
            <Box>
              <Text fontSize='md' fontWeight='semibold' mb={3}>
                Available Trade Licenses
              </Text>

              {isLoadingDedDetails ? (
                <Box display='flex' justifyContent='center' py={8}>
                  <Spinner size='lg' color='blue.500' />
                </Box>
              ) : (
                <Box border='1px solid' borderColor='gray.200' borderRadius='md' maxH='300px' overflowY='auto'>
                  <TableContainer>
                    <Table size='sm'>
                      <Thead bg='gray.50'>
                        <Tr>
                          <Th>Business Name</Th>
                          <Th>License Number</Th>
                          <Th>Status</Th>
                          <Th width='80px'>Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredDedDetails.length > 0 ? (
                          filteredDedDetails.map((detail) => (
                            <Tr
                              key={detail.id}
                              bg={selectedDetail?.id === detail.id ? 'blue.50' : 'white'}
                              _hover={{ bg: 'gray.50' }}>
                              <Td>
                                <Text fontSize='sm' fontWeight='medium'>
                                  {detail.businessName || 'N/A'}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize='sm'>{detail.licenseNumber}</Text>
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={
                                    detail.licenseStatus?.toLowerCase().includes('active') ? 'green' : 'gray'
                                  }
                                  size='sm'>
                                  {detail.licenseStatus || 'Unknown'}
                                </Badge>
                              </Td>
                              <Td>
                                <Tooltip label='Select this license'>
                                  <IconButton
                                    aria-label='Select license'
                                    icon={<FiCheck />}
                                    size='sm'
                                    colorScheme={selectedDetail?.id === detail.id ? 'blue' : 'gray'}
                                    variant={selectedDetail?.id === detail.id ? 'solid' : 'outline'}
                                    onClick={() => handleSelectDetail(detail)}
                                  />
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={4} textAlign='center' py={8}>
                              <Text color='gray.500'>
                                {searchTerm ? 'No results found for your search' : 'No trade licenses available'}
                              </Text>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>

            {/* Selected License Details */}
            {selectedDetail && (
              <>
                <Divider />
                <Box p={4} bg='green.50' borderRadius='md' border='1px solid' borderColor='green.200'>
                  <Text fontSize='sm' fontWeight='medium' color='green.800' mb={2}>
                    Selected License:
                  </Text>
                  <VStack align='start' spacing={1}>
                    <Text fontSize='sm'>
                      <strong>Business:</strong> {selectedDetail.businessName || 'N/A'}
                    </Text>
                    <Text fontSize='sm'>
                      <strong>License:</strong> {selectedDetail.licenseNumber}
                    </Text>
                    <Text fontSize='sm'>
                      <strong>Status:</strong> {selectedDetail.licenseStatus}
                    </Text>
                  </VStack>
                </Box>

                {/* Confidence Level */}
                <Controller
                  control={control}
                  name='licenseConfidence'
                  rules={{
                    required: 'Confidence level is required',
                    min: { value: 1, message: 'Confidence must be at least 1%' },
                    max: { value: 100, message: 'Confidence cannot exceed 100%' },
                  }}
                  render={({ field }) => (
                    <FormControl isInvalid={!!errors.licenseConfidence}>
                      <FormLabel>Confidence Level (%)</FormLabel>
                      <NumberInput {...field} min={1} max={100} defaultValue={95}>
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormErrorMessage>{errors.licenseConfidence?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                {/* Action Buttons */}
                <Divider />
                <HStack justify='flex-end' spacing={3}>
                  <Button variant='outline' onClick={onCloseModal} disabled={isUpdating}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme='blue'
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isUpdating}
                    loadingText='Updating...'>
                    Update License Mapping
                  </Button>
                </HStack>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
